<template>
  <div class="quote-details">
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading && quote"
      class="quote-details-content"
    >
      <div class="quote-details-header">
        <div class="title-text">
          {{ titleText }}
        </div>
        <div class="actions">
          <LockingButton
            v-if="quote.id > 0"
            class="duplicate"
            :locked="creatingQuote"
            :text="'Duplicate'"
            @click="onDuplicateClicked"
          />
          <LockingButton
            v-if="quote.id > 0 && !quote.sentForCustomerApproval && !quote.approvedDate"
            class="received-customer-approval-button"
            :locked="receivedCustomerApproval"
            :text="'Received Customer Approval?'"
            @click="onReceivedCustomerApprovalClicked"
          />
          <LockingButton
            v-if="quote.id > 0 && !quote.sentForCustomerApproval && !quote.approvedDate"
            class="send-to-customer-button"
            :locked="sendingQuoteToCustomer"
            :text="'Send To Customer'"
            @click="onSendQuoteToCustomerForApprovalClicked"
          />
          <LockingButton
            v-if="quote.id > 0 && quote.approvedDate"
            class="convert-button"
            :locked="convertingToOrder"
            :text="'Convert To Order'"
            @click="onConvertToOrderClicked"
          />
          <LockingButton
            class="save-button"
            :disabled="!isDirty"
            :locked="saving"
            :text="'Save'"
            @click="onSaveQuoteClicked"
          />
        </div>
      </div>
      <div class="left-column">
        <div class="card">
          <div class="header">
            General Information
          </div>
          <div class="content">
            <Dropdown
              v-model="quote.buildTypeId"
              label="Build Type"
              :show-inactive="quote.id > 0"
              :show-required="true"
              :options="buildTypeDropdownOptions"
              class="input"
            />
            <CartridgeSearchMachine
              v-if="cartridgeRequired"
              v-model="quote.cartridgeId"
              :show-clear="true"
              :show-required="true"
              class="input"
            />
            <NumericInput
              v-model="quote.partsDiscountPercentage"
              :show-required="true"
              label="Parts Discount %"
              class="input"
            />
            <NumericInput
              v-model="quote.servicesDiscountPercentage"
              :show-required="true"
              label="Services Discount %"
              class="input"
            />
            <div class="input-label">
              Total
            </div>
            <div class="total">
              {{ $methods.formatPrice(quote.total) }}
            </div>
            <AuditInformation 
              v-if="!loading && quote.id > 0 && quote.createdByUser && quote.createdDate && quote.lastModifiedByUser && quote.lastModifiedDate"
              :created-by-user="quote.createdByUser"
              :created-date="quote.createdDate"
              :last-modified-by-user="quote.lastModifiedByUser"
              :last-modified-date="quote.lastModifiedDate"
            />
            <div
              v-if="quote.id > 0 && quote.approvedDate && quote.approvedByUser"
              class="approval-info"
            >
              Approved by {{ quote.approvedByUser.firstName }} {{ quote.approvedByUser.lastName }} {{ $methods.formatDate(quote.approvedDate) }}
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Customer Information
          </div>
          <div class="content">
            <CustomerSearchMachine
              v-if="!quote.customer"
              :show-required="true"
              @customer="onCustomerSelected"
            />
            <div
              v-else
              class="customer-tile"
            >
              <div class="customer-tile-content">
                <div
                  v-if="quote.customer.firstName || quote.customer.lastName"
                  class="name"
                >
                  {{ quote.customer.firstName }} {{ quote.customer.lastName }}
                </div>
                <div
                  v-if="
                    quote.customer.addressLine1 ||
                      quote.customer.addressLine2 ||
                      quote.customer.city ||
                      stateDisplayValue ||
                      quote.customer.postalCode
                  "
                  class="address"
                >
                  <div v-if="quote.customer.addressLine1">
                    {{ quote.customer.addressLine1 }}
                  </div>
                  <div v-if="quote.customer.addressLine2">
                    {{ quote.customer.addressLine2 }}
                  </div>
                  <div
                    v-if="
                      quote.customer.city ||
                        stateDisplayValue ||
                        quote.customer.postalCode
                    "
                  >
                    <span
                      v-if="quote.customer.city"
                    >{{ quote.customer.city }}, </span><span v-if="stateDisplayValue">{{
                      stateDisplayValue + " "
                    }}</span><span v-if="quote.customer.postalCode">{{
                      quote.customer.postalCode
                    }}</span>
                  </div>
                </div>
                <a
                  v-if="quote.customer.phoneNumber"
                  :href="'tel:' + quote.customer.phoneNumber"
                >
                  {{ $methods.formatPhoneNumber(quote.customer.phoneNumber) }}
                </a>
                <a
                  v-if="quote.customer.email"
                  :href="'mailto:' + quote.customer.email"
                >
                  {{ quote.customer.email }}
                </a>
              </div>
              <DotsSvg @click.stop="showMenu = true" />
              <div
                v-if="showMenu"
                class="menu"
              >
                <div v-click-outside="closeMenu">
                  <div
                    class="menu-option"
                    @click="onEditCustomerClicked"
                  >
                    Edit Customer
                  </div>
                  <div
                    v-if="!quote || quote.id === 0"
                    class="menu-option"
                    @click="onRemoveCustomerClicked"
                  >
                    Remove Customer
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="quote.id > 0"
          class="card"
        >
          <div class="header">
            Notes
          </div>
          <div class="content">
            <TextArea
              v-model="newNoteText"
              :show-required="true"
            />
            <LockingButton
              class="add"
              :disabled="!addNoteEnabled"
              :locked="addingNote"
              :text="'Add'"
              @click="onAddNoteClicked"
            />
            <NoteTile
              v-for="note in quote.notes"
              :key="note.id"
              :note="note"
              @delete="onDeleteNote"
              @edit="onEditNote"
            />
          </div>
        </div>
      </div>
      <div class="right-column">
        <div class="card">
          <div class="header">
            Customer Supplied Parts
          </div>
          <div class="content">
            <TextArea v-model="quote.customerSuppliedParts" />
          </div>
        </div>
        <div
          v-if="!quote || quote.id === 0"
          class="message"
        >
          * Please save the quote once the build type and customer are selected. Once the quote is saved, the additional information can be added.
        </div>
        <div
          v-if="quote.id > 0"
          class="card"
        >
          <div class="header">
            Parts

            <PlusSvg
              class="add"
              @click="onAddPartClicked"
            />
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort drag-and-drop">
                      <div />
                    </th>
                    <th class="no-sort">
                      <div>
                        Item
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                    <th class="no-sort" />
                  </tr>
                </thead>
                <draggable
                  v-if="quote.quoteParts.length > 0"
                  tag="tbody"
                  :list="quote.quoteParts"
                  item-key="id"
                  @change="onQuotePartsDisplayOrderChanged"
                >
                  <template #item="{element}">
                    <tr :class="element.active ? '' : 'inactive'">
                      <td><DragAndDropSvg /></td>
                      <td>{{ element.name }}</td>
                      <td>
                        <CurrencyInput
                          v-model="element.price"
                          @blur="onQuotePartChanged(element)"
                        />
                      </td>
                      <td class="notes">
                        <NoteTile
                          v-for="note in element.notes"
                          :key="note.id"
                          :note="note"
                          @delete="onDeleteNote"
                          @edit="onEditQuotePartNote"
                        />
                      </td>
                      <td class="delete">
                        <CommentSvg
                          class="add-comment"
                          @click.stop="onAddQuotePartNoteClicked(element.id)"
                        />
                        <TrashCanSvg @click.stop="onRemoveQuotePartClicked(element.id)" />
                      </td>
                    </tr>
                  </template>
                </draggable>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="5"
                      class="message"
                    >
                      There are no parts on the quote. Please click the '+' above to add a part.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="footer">
            Total: {{ $methods.formatPrice(quote.partsTotal) }}
          </div>
        </div>
        <div
          v-if="quote.id > 0"
          class="card"
        >
          <div class="header">
            Services

            <PlusSvg
              class="add"
              @click="onAddServiceClicked"
            />
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort drag-and-drop">
                      <div />
                    </th>
                    <th class="no-sort">
                      <div>
                        Name
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Description
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Fields
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                    <th class="no-sort" />
                  </tr>
                </thead>
                <draggable
                  v-if="quote.quoteServices.length > 0"
                  tag="tbody"
                  :list="quote.quoteServices"
                  item-key="id"
                  @change="onQuoteServicesDisplayOrderChanged"
                >
                  <template #item="{element}">
                    <tr :class="element.active ? '' : 'inactive'">
                      <td> <DragAndDropSvg /> </td>
                      <td>{{ element.name }}</td>
                      <td class="description">
                        {{ element.description }}
                      </td>
                      <td>
                        <CurrencyInput
                          v-model="element.price"
                          @blur="onQuoteServiceChanged(element)"
                        />
                      </td>
                      <td class="services">
                        <div
                          v-for="(serviceFieldValue, index) in element.serviceFieldValues"
                          :key="index"
                          class="service"
                        >
                          <TextInput
                            v-model="serviceFieldValue.value"
                            :label="serviceFieldValue.name"
                            class="input"
                            :disabled="true"
                          />
                        </div>
                      </td>
                      <td class="notes">
                        <NoteTile
                          v-for="note in element.notes"
                          :key="note.id"
                          :note="note"
                          @delete="onDeleteNote"
                          @edit="onEditQuoteServiceNote"
                        />
                      </td>
                      <td class="delete">
                        <CommentSvg
                          class="add-comment"
                          @click.stop="onAddServiceNoteClicked(element.id)"
                        />
                        <TrashCanSvg @click.stop="onRemoveQuoteServiceClicked(element.id)" />
                      </td>
                    </tr>
                  </template>
                </draggable>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="7"
                      class="message"
                    >
                      There are no services on the quote. Please click the '+' above to add a service.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="footer">
            Total: {{ $methods.formatPrice(quote.servicesTotal) }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <transition name="slide-up">
    <NoteModal 
      v-if="showNoteModal && noteToEdit"
      :note="noteToEdit"
      :quote-id="id"
      @note-changed="getQuote"
      @close="showNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <NoteModal 
      v-if="showQuoteServiceNoteModal && quoteServiceNoteToEdit"
      :note="quoteServiceNoteToEdit"
      :quote-service-id="quoteServiceId"
      :show-customer-visible-option="true"
      @note-changed="getQuote"
      @close="showQuoteServiceNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <NoteModal 
      v-if="showQuotePartNoteModal && quotePartNoteToEdit"
      :note="quotePartNoteToEdit"
      :quote-part-id="quotePartId"
      :show-customer-visible-option="true"
      @note-changed="getQuote"
      @close="showQuotePartNoteModal = false"
    />
  </transition>
  <transition name="slide-up">
    <AddPartModal 
      v-if="showAddPartModal"
      :quote-id="id"
      @part-added="getQuote"
      @close="showAddPartModal = false"
    />
  </transition>
  <transition name="slide-up">
    <AddServiceModal 
      v-if="showAddServiceModal"
      :quote-id="id"
      @service-added="getQuote"
      @close="showAddServiceModal = false"
    />
  </transition>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { QuoteService } from '@/services/quote.service'
import { QuoteDetails } from '@/@types/responses/quote/QuoteDetails'
import { BuildTypeService } from '@/services/buildType.service'
import CustomerSearchMachine from '@/components/elements/CustomerSearchMachine.vue'
import NoteTile from '@/components/elements/NoteTile.vue'
import PlusSvg from '@/components/svgs/PlusSvg.vue'
import { Note } from '@/@types/responses/note/Note'
import NoteModal from '@/components/modals/NoteModal.vue'
import AddPartModal from '@/components/modals/AddPartModal.vue'
import AddServiceModal from '@/components/modals/AddServiceModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { Customer } from '@/@types/responses/customer/Customer'
import TextArea from '@/components/shared/TextArea.vue'
import { NoteService } from '@/services/note.service'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import CommentSvg from '@/components/svgs/CommentSvg.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { QuotePartService } from '@/services/quotePart.service'
import { QuoteServiceService } from '@/services/quoteService.service'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { CreateQuoteMessage } from '@/@types/messages/CreateQuoteMessage'
import { UpdateQuoteMessage } from '@/@types/messages/UpdateQuoteMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { QuoteServiceDetails } from '@/@types/responses/quote/QuoteServiceDetails'
import { QuotePartDetails } from '@/@types/responses/quote/QuotePartDetails'
import { UpdateQuotePartMessage } from '@/@types/messages/UpdateQuotePartMessage'
import { UpdateQuoteServiceMessage } from '@/@types/messages/UpdateQuoteServiceMessage'
import DragAndDropSvg from '@/components/svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import { UpdateQuotePartsMessage } from '@/@types/messages/UpdateQuotePartsMessage'
import { UpdateQuoteServicesMessage } from '@/@types/messages/UpdateQuoteServicesMessage'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Quote',
  components: {
    AddPartModal,
    AddServiceModal,
    AuditInformation,
    CartridgeSearchMachine,
    CommentSvg,
    CurrencyInput,
    CustomerSearchMachine,
    DotsSvg,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    NumericInput,
    PlusSvg,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showAddPartModal || this.showAddServiceModal || this.showNoteModal || this.showQuotePartNoteModal || this.showQuoteServiceNoteModal) {
      this.showAddPartModal = false
      this.showAddServiceModal = false
      this.showNoteModal = false
      this.showQuotePartNoteModal = false
      this.showQuoteServiceNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const quotePartService = inject(PROVIDER_CONSTANTS.quotePartService) as QuotePartService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const quoteServiceService = inject(PROVIDER_CONSTANTS.quoteServiceService) as QuoteServiceService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildTypeService,
      noteService,
      quotePartService,
      quoteService,
      quoteServiceService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let quote: QuoteDetails|undefined
    let quotePartNoteToEdit: Note|undefined
    let quoteServiceNoteToEdit: Note|undefined
    let unchangedQuote: QuoteDetails|undefined

    return {
      addingNote: false,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      convertingToOrder: false,
      creatingQuote: false,
      loading: true,
      newNoteText: '',
      noteToEdit,
      quote,
      quotePartId: 0,
      quotePartNoteToEdit,
      quoteServiceId: 0,
      quoteServiceNoteToEdit,
      receivedCustomerApproval: false,
      saving: false,
      sendingQuoteToCustomer: false,
      showAddPartModal: false,
      showAddServiceModal: false,
      showMenu: false,
      showNoteModal: false,
      showQuotePartNoteModal: false,
      showQuoteServiceNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedQuote
    }
  },
  computed: {
    addNoteEnabled(): boolean {
      return this.newNoteText.trim() !== ''
    },
    cartridgeRequired(): boolean {
      const index = this.buildTypeDropdownOptions.findIndex(b => b.id === this.quote?.buildTypeId)
      if (index === -1)
        return false

      return this.buildTypeDropdownOptions[index].cartridgeRequired
    },
    isDirty(): boolean {
      const a = this.quote
      const b = this.unchangedQuote

      let validCartridgeValue = true
      if (this.cartridgeRequired && !a?.cartridgeId)
        validCartridgeValue = false

      return (a?.cartridgeId !== b?.cartridgeId ||
             a?.customerId !== b?.customerId ||
             a?.buildTypeId !== b?.buildTypeId ||
             a?.partsDiscountPercentage !== b?.partsDiscountPercentage ||
             a?.servicesDiscountPercentage !== b?.servicesDiscountPercentage ||
             a?.customerSuppliedParts !== b?.customerSuppliedParts) && (a?.buildTypeId !== undefined && a?.customerId !== undefined && validCartridgeValue && a?.partsDiscountPercentage !== undefined && a?.partsDiscountPercentage >= 0 && a?.servicesDiscountPercentage !== undefined && a?.servicesDiscountPercentage >= 0)
    },
    stateDisplayValue(): string|undefined {
      if (this.quote && this.quote.customer?.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.quote?.customer?.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    },
    titleText(): string {
      return !this.quote || this.quote.id === 0 ? 'Create Quote' : `Quote #${this.id}`
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    Promise.all([this.getBuildTypes(), this.getStates()]).then(() => {
      if (this.id > 0) {
        this.getQuote()
      } else {
        this.quote = new QuoteDetails(0)
        this.unchangedQuote = new QuoteDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getBuildTypes(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getQuote(): Promise<void> {
      return this.quoteService.getQuote(this.quote ? this.quote.id : this.id).then(quote => {
        this.quote = quote
        this.unchangedQuote = JSON.parse(JSON.stringify(quote))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false
        this.sendingQuoteToCustomer = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the quote. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onAddNoteClicked(): void {
      this.addingNote = true
      const noteMessage = new NoteMessage(this.newNoteText, false)
      this.quoteService.createQuoteNote(this.id, noteMessage).then(() => {
        this.getQuote().then(() => {
          this.newNoteText = ''
          this.addingNote = false
          this.toast.success('Note added successfully.')
        })
      }).catch(error => {
        this.addingNote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to save the note. Please try again.')
        }
      })
    },
    onAddPartClicked(): void {
      this.showAddPartModal = true
    },
    onAddQuotePartNoteClicked(quotePartId: number): void {
      this.quotePartId = quotePartId
      this.quotePartNoteToEdit = new Note(0)
      this.showQuotePartNoteModal = true
    },
    onAddServiceClicked(): void {
      this.showAddServiceModal = true
    },
    onAddServiceNoteClicked(quoteServiceId: number): void {
      this.quoteServiceId = quoteServiceId
      this.quoteServiceNoteToEdit = new Note(0)
      this.showQuoteServiceNoteModal = true
    },
    onConvertToOrderClicked(): void {
      this.convertingToOrder = true
      this.quoteService.createOrder(this.id).then(objectCreatedResponse => {
        this.convertingToOrder = false
        this.$router.replace(`/order/${objectCreatedResponse.id}`)
      }).catch(error => {
        this.convertingToOrder = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to covert this quote to an order. Please try again.')
        }
      })
    },
    onCustomerSelected(customer: Customer): void {
      if (!this.quote) return

      this.quote.customer = customer
      this.quote.customerId = customer.id
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDuplicateClicked(): void {
      this.creatingQuote = true

      this.quoteService.createQuoteFromQuote(this.id).then(objectCreatedResponse => {
        this.toast.success('A new quote was created from the existing quote successfully.')
        window.location.href = '/quote/' + objectCreatedResponse.id
      }).catch(error => {
        this.creatingQuote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to duplicate the quote as a new quote. Please try again.')
        }
      })
    },
    onEditCustomerClicked(): void {
      if (!this.quote || !this.quote.customerId) return

      const routeData = this.$router.resolve('/customer/' + this.quote.customerId)
      window.open(routeData.href, '_blank')
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onEditQuotePartNote(note: Note): void {
      this.quotePartNoteToEdit = note
      this.showQuotePartNoteModal = true
    },
    onEditQuoteServiceNote(note: Note): void {
      this.quoteServiceNoteToEdit = note
      this.showQuoteServiceNoteModal = true
    },
    onQuotePartChanged(quotePart: QuotePartDetails): void {
      const updateQuotePartMessage = new UpdateQuotePartMessage(quotePart.price)
      this.quotePartService.updateQuotePart(quotePart.id, updateQuotePartMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onQuotePartsDisplayOrderChanged(): void {
      if (!this.quote) return

      const updateQuotePartsMessage = new UpdateQuotePartsMessage(this.quote.quoteParts.map(q => q.id))
      this.quotePartService.updateQuoteParts(updateQuotePartsMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onQuoteServiceChanged(quoteService: QuoteServiceDetails): void {
      const updateQuoteServiceMessage = new UpdateQuoteServiceMessage(quoteService.price)
      this.quoteServiceService.updateQuoteService(quoteService.id, updateQuoteServiceMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onQuoteServicesDisplayOrderChanged(): void {
      if (!this.quote) return

      const updateQuoteServicesMessage = new UpdateQuoteServicesMessage(this.quote.quoteServices.map(q => q.id))
      this.quoteServiceService.updateQuoteServices(updateQuoteServicesMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onReceivedCustomerApprovalClicked(): void {
      if (!this.quote || this.receivedCustomerApproval || !this.quote.customerId || !this.quote.buildTypeId) return

      this.receivedCustomerApproval = true

      const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, true, false, this.quote.servicesDiscountPercentage)
      this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The quote was approved successfully.')
          this.receivedCustomerApproval = false
        })
      }).catch(error => {
        this.receivedCustomerApproval = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to approve the quote. Please try again.')
        }
      })
    },
    onRemoveCustomerClicked(): void {
      if (!this.quote) return

      this.quote.customer = undefined
      this.quote.customerId = undefined
    },
    onRemoveQuotePartClicked(id: number): void {
      this.quotePartService.deleteQuotePart(id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The part was removed from the quote successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the part from the quote. Please try again.')
        }
      })
    },
    onRemoveQuoteServiceClicked(id: number): void {
      this.quoteServiceService.deleteQuoteService(id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The service was removed from the quote successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the service from the quote. Please try again.')
        }
      })
    },
    onSaveQuoteClicked(): void {
      if (!this.quote || !this.quote.customerId || !this.quote.buildTypeId) return

      this.saving = true

      if (this.quote.id === 0) {
        const createQuoteMessage = new CreateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, this.quote.servicesDiscountPercentage)
        this.quoteService.createQuote(createQuoteMessage).then(objectCreatedResponse => {
          if (!this.quote) return
          
          this.quote.id = objectCreatedResponse.id
          this.getQuote().then(() => {
            this.$router.replace(`/quote/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The quote was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the quote. Please try again.')
          }
        })
      } else {
        const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, false, false, this.quote.servicesDiscountPercentage)
        this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
          this.getQuote().then(() => {
            this.toast.success('The quote was updated successfully.')
            this.saving = false
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the quote. Please try again.')
          }
        })
      }
    },
    onSendQuoteToCustomerForApprovalClicked(): void {
      if (!this.quote || this.sendingQuoteToCustomer || !this.quote.customerId || !this.quote.buildTypeId) return

      this.sendingQuoteToCustomer = true

      const updateQuoteMessage = new UpdateQuoteMessage(this.quote.buildTypeId, this.quote.cartridgeId, this.quote.customerSuppliedParts, this.quote.partsDiscountPercentage, false, true, this.quote.servicesDiscountPercentage)
      this.quoteService.updateQuote(this.id, updateQuoteMessage).then(() => {
        this.getQuote().then(() => {
          this.toast.success('The quote was sent to the customer successfully.')
          this.sendingQuoteToCustomer = false
        })
      }).catch(error => {
        this.sendingQuoteToCustomer = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to send the quote to the customer. Please try again.')
        }
      })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.quote)
        document.title = `Quote #${this.quote.id} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.quote-details {
  background-color: $Color-fill-gray;
  position: relative;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .loading-spinner {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, 50%);
  }

  > .quote-details-content {
    display: grid;
    column-gap: 20px;
    grid-template-areas: 
      "header"
      "left"
      "right";
      grid-template-columns: 100%;

    @include desktop {
      grid-template-columns: 340px minmax(0, 1fr);
      grid-template-areas:
        "header header"
        "left right";
    }

    > .quote-details-header {
      grid-area: header;
      padding: 15px 15px 10px 15px;
      margin-bottom: 15px;
      border-bottom: 1px solid $Color-border-gray;
      display: flex;
      flex-direction: column;
      position: sticky;
      background-color: $Color-fill-gray;
      z-index: 100;
      top: 94px;

      @include desktop {
        flex-direction: row;
        top: 130px;
      }

      > .title-text {
        line-height: 37px;
        font-size: 24px;
        font-weight: bold;
      }

      > .actions {
        position: relative;
        display: flex;
        margin-left: auto;

        > .duplicate {
          margin-left: auto;
        }

        > .convert-button,
        > .received-customer-approval-button,
        > .send-to-customer-button,
        > .save-button {
          margin-left: 15px;
        }
      }
    }

    > .left-column {
      grid-area: left;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-right: 0;
      }
    }

    > .right-column {
      grid-area: right;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-left: 0;
      }

      > .message {
        font-style: italic;
        color: $Color-font-medium;
      }
    }

    .card {
      background-color: $Color-white;
      margin-bottom: 15px;
      @include element-shadow;

      > .header {
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
        position: relative;

        > .add {
          fill: $Color-white;
          position: absolute;
          right: 15px;
          top: 15px;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }

      > .content {
        padding: 15px;

        > .input {
          margin-bottom: 10px;
        }

        > .input-label {
          @include input-text-field-label;
        }

        > .total {
          margin-bottom: 15px;
          font-weight: 600;
          color: $Color-font-high;
          font-size: 1.3rem;
        }

        > .approval-info {
          margin-top: 15px;
          font-size: 12px;
          line-height: 18px;
          font-weight: normal;
          color: $Color-font-medium;
        }

        > .add {
          @include positive-button;
          margin-top: 10px;
          margin-bottom: 15px;
        }

        > .customer-tile {
          position: relative;
          background-color: $Color-fill-gray;
          padding: 15px;
          border-radius: 6px;
          display: flex;

          > .customer-tile-content {
            flex: 0 0 90%;
            display: flex;
            flex-direction: column;
            line-height: 24px;

            > .name {
              color: $Color-font-high;
              font-weight: 600;
            }

            > .address {
              color: $Color-font-medium;
              font-size: 15px;
            }

            > a {
              color: $Color-font-medium;
              text-decoration: none;
              font-style: italic;
              font-size: 15px;
            }
          }

          > svg {
            flex: 0 0 10%;
            margin: auto 10px;
          }

          > .menu {
            @include menu;
          }
        }

        > .overflow-wrapper {
          overflow-y: auto;
          @include sac-table;

          td.description {
            white-space: break-spaces;
          }

          td.services {
            padding-top: 15px;
            min-width: 450px;

            > .service {
              margin-bottom: 15px;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          }

          td.notes {
            min-width: 300px;
          }
        }

        .add-comment {
          margin-right: 10px;
        }
      }

      > .footer {
        text-align: right;
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
      }
    }
  }
}

.slide-up {
  @include modal-animation;
}
</style>
